import React from 'react';
/**
 * Helper to account for syntax differences between react helmet
 * and vanilla React when it comes to setting inline JS for script tags.
 */

function createInlineJSScriptTag(_ref) {
  var key = _ref.key,
      inlineJs = _ref.inlineJs,
      useHelmetSyntax = _ref.useHelmetSyntax;

  if (useHelmetSyntax) {
    return (
      /*#__PURE__*/
      React.createElement("script", {
        "data-testid": key,
        key: key,
        type: "text/javascript"
      }, inlineJs)
    );
  } // This is vanilla React way of setting inline JS in script tag.


  return (
    /*#__PURE__*/
    React.createElement("script", {
      "data-testid": key,
      dangerouslySetInnerHTML: {
        __html: inlineJs
      },
      key: key,
      type: "text/javascript"
    })
  );
}
/**
 * Configuration per environment.
 */


var googleOptimizeId = function googleOptimizeId(appEnv) {
  switch (appEnv) {
    case 'prod':
      return 'GTM-WPLPD66';

    case 'staging':
      return 'GTM-MD4FMWK';

    default:
      return 'GTM-PP7CRFJ';
  }
};

var googleAnalyticsId = function googleAnalyticsId(appEnv) {
  switch (appEnv) {
    case 'prod':
      return 'UA-61672052-4';

    case 'staging':
      return 'UA-61672052-8';

    default:
      return 'UA-147017748-1';
  }
};

var googleTagManagerId = function googleTagManagerId(appEnv) {
  switch (appEnv) {
    case 'prod':
      return 'GTM-TKJJBGW';

    case 'staging':
      return 'GTM-KGL4MWS';

    default:
      return 'GTM-K4F758H';
  }
};

var GOOGLE_ADWORDS_ID = 'AW-879442427';
/**
 * This component should be placed in the document head so GA can do its magic.
 * In this case we choose to place in head over body because we want GA to
 * start reporting metrics as soon as possible.
 */

function LoadGoogleAnalytics(_ref) {
  var _ref$appEnv = _ref.appEnv,
      appEnv = _ref$appEnv === void 0 ? 'dev' : _ref$appEnv,
      _ref$includeOptimize = _ref.includeOptimize,
      includeOptimize = _ref$includeOptimize === void 0 ? false : _ref$includeOptimize,
      _ref$includeTagManage = _ref.includeTagManager,
      includeTagManager = _ref$includeTagManage === void 0 ? false : _ref$includeTagManage,
      _ref$useHelmetSyntax = _ref.useHelmetSyntax,
      useHelmetSyntax = _ref$useHelmetSyntax === void 0 ? false : _ref$useHelmetSyntax;
  var optimizeId = googleOptimizeId(appEnv);
  var analyticsId = googleAnalyticsId(appEnv);
  var tagManagerId = googleTagManagerId(appEnv);
  var result = [];

  if (includeOptimize) {
    // Page hiding snippet (recommended). Reduces page flicker for optimize experiments.
    var optimizeInlineJs = "\n      (function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;\n      h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};\n      (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;\n      })(window,document.documentElement,'async-hide','dataLayer',4000,\n      {'".concat(optimizeId, "':true})\n    ");
    result.push(
    /*#__PURE__*/
    React.createElement("style", {
      "data-testid": "optimize-page-hide-css",
      key: "optimize-page-hide-css"
    }, " .async-hide { opacity: 0 !important} "), createInlineJSScriptTag({
      key: 'optimize-page-hide-js',
      inlineJs: optimizeInlineJs,
      useHelmetSyntax: useHelmetSyntax
    }));
  } // Global site tag (gtag.js); Google Analytics + Google Optimize + Google Adwords


  var gtagInlineJs = "\n    window.dataLayer = window.dataLayer || [];\n    function gtag(){dataLayer.push(arguments);}\n    // creates a google analytics cookie that is cross-domain\n    // so multiple are not created when we move from one domain to another\n    gtag('create', '".concat(analyticsId, "', 'auto');\n    gtag('js', new Date());\n    gtag('config', '").concat(analyticsId, "'").concat(includeOptimize ? ", { 'optimize_id': '".concat(optimizeId, "' }") : '', ");\n  ");
  result.push(
  /*#__PURE__*/
  React.createElement("script", {
    async: true,
    "data-testid": "gtag-script",
    key: "gtag-script",
    src: "https://www.googletagmanager.com/gtag/js?id=".concat(analyticsId)
  }), createInlineJSScriptTag({
    key: 'gtag-js',
    inlineJs: gtagInlineJs,
    useHelmetSyntax: useHelmetSyntax
  }));

  if (appEnv === 'prod') {
    // Google Adwords integration. Only makes sense in prod where we actually run ads.
    var adWordsInlineJs = "\n      window.gtag('config', '".concat(GOOGLE_ADWORDS_ID, "');\n    ");
    result.push(createInlineJSScriptTag({
      key: 'adwords',
      inlineJs: adWordsInlineJs,
      useHelmetSyntax: useHelmetSyntax
    }));
  }

  if (includeTagManager) {
    // Google Tag Manager
    var tagManagerInlineJs = "\n      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push(\n      {'gtm.start': new Date().getTime(),event:'gtm.js'}\n      );var f=d.getElementsByTagName(s)[0],\n      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);\n      })(window,document,'script','dataLayer','".concat(tagManagerId, "');\n    ");
    result.push(createInlineJSScriptTag({
      key: 'tag-manager-js',
      inlineJs: tagManagerInlineJs,
      useHelmetSyntax: useHelmetSyntax
    }));
  }

  return result;
}
/**
 * Common Google Analytics event tracking.
 * Apps will typically create their own customized events that use these.
 */


var trackPageView = function trackPageView(location) {
  if (window.gtag) {
    window.gtag('set', {
      'page_path': location.pathname + location.search
    });
    window.gtag('event', 'page_view', {
      'page_path': location.pathname
    });
  }
};

var trackEvent = function trackEvent(category, action, label, value) {
  if (window.gtag) {
    window.gtag('event', action, {
      'event_category': category,
      'event_label': label,
      'value': value
    });
  }
};

export { GOOGLE_ADWORDS_ID, LoadGoogleAnalytics, googleAnalyticsId, googleOptimizeId, googleTagManagerId, trackEvent, trackPageView };